import { useEffect, useMemo, useState, useRef } from "react";
import { FiAlertTriangle, FiPlus } from "react-icons/fi";
import { createBrand, updateTokenBrand, updateCertificateBrand } from "../../../Api/Network";
import AccessToggleCard from "./AccessToggleCard";
import ToggleSwitch from "./ToggleSwitch";
import { RiRefreshLine } from "react-icons/ri";

function BrandCreateForm({ initialValues, mode = "create", onSubmit, onCancel }) {
    const initialForm = useMemo(
        () => ({
            name: "",
            domain: "",
            description: "",
            language: "fa",
            isActive: true,
            accessOwnBrand: true,
            accessOtherBrands: false,
        }),
        []
    );

    const [form, setForm] = useState(initialForm);
    const [formErrors, setFormErrors] = useState({ name: "", domain: "" });
    const [formError, setFormError] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isUpdatingToken, setIsUpdatingToken] = useState(false);
    const [isUpdatingCertificate, setIsUpdatingCertificate] = useState(false);

    useEffect(() => {
        if (initialValues) {
            setForm({
                name: initialValues.name || "",
                domain: initialValues.domain || initialValues.domainName || "",
                description: initialValues.description || "",
                language: initialValues.language || initialValues.lang || (initialValues.langType === "ENGLISH" ? "en" : "fa") || "fa",
                isActive: initialValues.isActive ?? true,
                accessOwnBrand: initialValues.accessOwnBrand ?? initialValues.policy?.isAccessOwnBrnad ?? true,
                accessOtherBrands: initialValues.accessOtherBrands ?? initialValues.policy?.isAccessOtherBrand ?? false,
            });
            setFormErrors({ name: "", domain: "" });
            setFormError("");
            return;
        }
        setForm(initialForm);
        setFormErrors({ name: "", domain: "" });
        setFormError("");
    }, [initialValues, initialForm]);

    const updateField = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
        setFormErrors((prev) => ({ ...prev, [key]: "" }));
    };

    const clearForm = () => {
        setForm(initialForm);
        setFormErrors({ name: "", domain: "" });
        setFormError("");
    };

    const submit = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        const cleanedName = form.name.trim();
        const cleanedDomain = form.domain.trim();
        const errors = { name: "", domain: "" };

        if (!cleanedName) errors.name = "نام برند را وارد کنید.";
        if (!cleanedDomain) errors.domain = "دامنه برند را وارد کنید.";
        if (errors.name || errors.domain) {
            setFormErrors(errors);
            return;
        }

        const payload = {
            id: initialValues?.id,
            name: cleanedName,
            domainName: cleanedDomain,
            description: form.description.trim(),
            langType: form.language === "en" ? "ENGLISH" : "PERSIAN",
            isActive: form.isActive,
            policy: {
                isAccessOwnBrnad: form.accessOwnBrand,
                isAccessOtherBrand: form.accessOtherBrands,
            },
        };

        try {
            setFormError("");
            setIsSubmitting(true);
            if (mode === "create") {
                const token = localStorage.getItem("token");
                const response = await createBrand(token, payload);
                onSubmit(response?.data ?? payload);
            } else {
                onSubmit(payload);
            }
            clearForm();
        } catch (error) {
            console.error("submit brand form error", error);
            setFormError("خطا در ذخیره‌سازی برند. لطفاً دوباره تلاش کنید.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleUpdateToken = async () => {
        if (!initialValues?.id) {
            console.error("ID برند یافت نشد");
            return;
        }

        try {
            setIsUpdatingToken(true);
            const token = localStorage.getItem("token");
            const response = await updateTokenBrand(token, initialValues.id);
            console.log("توکن بروزرسانی شد:", response);
            // می‌تونید پیام موفقیت نمایش بدید
        } catch (error) {
            console.error("خطا در بروزرسانی توکن:", error);
            // می‌تونید پیام خطا نمایش بدید
        } finally {
            setIsUpdatingToken(false);
        }
    };

    const handleUpdateCertificate = async () => {
        if (!initialValues?.id) {
            console.error("ID برند یافت نشد");
            return;
        }

        try {
            setIsUpdatingCertificate(true);
            const token = localStorage.getItem("token");
            const response = await updateCertificateBrand(token, initialValues.id);
            console.log("سرتیفیکیت بروزرسانی شد:", response);
            // می‌تونید پیام موفقیت نمایش بدید
        } catch (error) {
            console.error("خطا در بروزرسانی سرتیفیکیت:", error);
            // می‌تونید پیام خطا نمایش بدید
        } finally {
            setIsUpdatingCertificate(false);
        }
    };

    return (
        <div className="space-y-6">
            <div >
                <div className="bg-gray-50 rounded-xl border border-gray-200 p-5 flex flex-col gap-4">

                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-bold text-gray-700 rtol">نام برند <span className="text-red-500">*</span></label>
                        <input
                            value={form.name}
                            onChange={(e) => updateField("name", e.target.value)}
                            className={
                                "w-full bg-white rounded-lg px-4 py-2 outline-none rtol border " +
                                (formErrors.name
                                    ? "border-red-500 focus:border-red-500"
                                    : "border-gray-300 focus:border-primary-500")
                            }
                            placeholder="مثلاً: Boostan"
                        />
                        {formErrors.name && (
                            <p className="text-xs text-red-600 mt-1 rtol">{formErrors.name}</p>
                        )}
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-bold text-gray-700 rtol">دامنه <span className="text-red-500">*</span></label>
                        <input
                            value={form.domain}
                            onChange={(e) => updateField("domain", e.target.value)}
                            className={
                                "w-full bg-white rounded-lg px-4 py-2 outline-none rtol border " +
                                (formErrors.domain
                                    ? "border-red-500 focus:border-red-500"
                                    : "border-gray-300 focus:border-primary-500")
                            }
                            placeholder="مثلاً: boostan.ir"
                        />
                        {formErrors.domain && (
                            <p className="text-xs text-red-600 mt-1 rtol">{formErrors.domain}</p>
                        )}
                    </div>

                    <div className="flex flex-col gap-2">
                        <label className="text-sm font-bold text-gray-700 rtol">
                            توضیحات (اختیاری)
                        </label>
                        <textarea
                            value={form.description}
                            onChange={(e) => updateField("description", e.target.value)}
                            className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 outline-none focus:border-primary-500 rtol min-h-[110px]"
                            placeholder="توضیح کوتاه درباره برند..."
                        />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex flex-col gap-2">
                            <label className="text-sm font-bold text-gray-700 rtol">زبان</label>
                            <select
                                value={form.language}
                                onChange={(e) => updateField("language", e.target.value)}
                                className="w-full bg-white border border-gray-300 rounded-lg px-4 py-2 outline-none focus:border-primary-500 rtol"
                            >
                                <option value="fa">فارسی</option>
                                <option value="en">English</option>
                            </select>
                        </div>

                        <div className="flex flex-col gap-2">
                            <label className="text-sm font-bold text-gray-700 rtol">وضعیت</label>
                            <div className="flex flex-row-reverse items-center justify-between bg-white border border-gray-300 rounded-lg px-4 py-2">
                                <span className="text-sm font-bold text-gray-800 rtol">
                                    {form.isActive ? "فعال" : "غیرفعال"}
                                </span>
                                <ToggleSwitch
                                    checked={form.isActive}
                                    onChange={(v) => updateField("isActive", v)}
                                />
                            </div>
                        </div>

                        <AccessToggleCard
                            title="دسترسی به سایر برند ها"
                            description="کاربران میتوانند به داده های سایر برند ها نیز دسترسی داشته باشند"
                            toggle={
                                <ToggleSwitch
                                    checked={form.accessOtherBrands}
                                    onChange={(v) => updateField("accessOtherBrands", v)}
                                />
                            }
                        />
                        <AccessToggleCard
                            title="دسترسی به برند خود"
                            description="کاربران فقط به داده های خود دسترسی دارند"
                            toggle={
                                <ToggleSwitch
                                    checked={form.accessOwnBrand}
                                    onChange={(v) => updateField("accessOwnBrand", v)}
                                />
                            }
                        />

                    </div>

                    {mode === "edit" && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <button
                                type="button"
                                onClick={handleUpdateToken}
                                disabled={isUpdatingToken}
                                className="w-full flex items-center justify-center rounded-lg border border-blue-200 bg-blue-50 text-blue-700 px-4 py-3 text-sm font-bold hover:bg-blue-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isUpdatingToken ? "در حال بروزرسانی..." : "بروزرسانی توکن"}
                                <RiRefreshLine className="text-xl ml-2" />
                            </button>
                            <button
                                type="button"
                                onClick={handleUpdateCertificate}
                                disabled={isUpdatingCertificate}
                                className="w-full flex items-center justify-center rounded-lg border border-green-200 bg-green-50 text-green-700 px-4 py-3 text-sm font-bold hover:bg-green-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isUpdatingCertificate ? "در حال بروزرسانی..." : "بروزرسانی سرتیفیکیت"}
                                <RiRefreshLine className="text-xl ml-2" />
                            </button>
                        </div>
                    )}

                    {formError && (
                        <div className="bg-red-50 border border-red-200 text-redCustom-500 rounded-lg px-4 py-2 rtol">
                            {formError}
                        </div>
                    )}
                </div>
            </div>

            <div className="flex flex-row-reverse justify-end gap-4 pt-4 border-t border-gray-200">
                <button
                    type="button"
                    onClick={() => onCancel && onCancel()}
                    className="px-6 py-2 w-full rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold rtol"
                >
                    لغو
                </button>
                <button
                    type="button"
                    onClick={submit}
                    disabled={isSubmitting}
                    className={
                        "px-6 py-2 w-full rounded-lg text-white font-bold rtol flex items-center justify-center gap-2 " +
                        (isSubmitting
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-primary-500 hover:bg-blue-800")
                    }
                >
                    {isSubmitting
                        ? mode === "edit"
                            ? "در حال بروز رسانی..."
                            : "در حال ایجاد..."
                        : mode === "edit"
                            ? "بروزرسانی برند"
                            : "ایجاد برند"}
                </button>
            </div>
        </div>
    );
}

export default BrandCreateForm;
